import { useState, useCallback, useRef } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTableData, useTableInfo } from '../../hooks/useMySQLData';
import { insertRow, updateRow, deleteRow, fetchTableRows } from '../../api/mysql';
import { usePermission } from '../../hooks/usePermission';
import { useSnackbar } from '../../context/SnackbarContext';

function fmtBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
}

function cellStr(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

interface NewRowDialogProps {
  db: string;
  table: string;
  columns: string[];
  onClose: () => void;
  onSaved: () => void;
}

function NewRowDialog({ db, table, columns, onClose, onSaved }: NewRowDialogProps) {
  const showSnackbar = useSnackbar();
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(columns.map((c) => [c, '']))
  );

  const mutation = useMutation({
    mutationFn: () => insertRow(db, table, values),
    onSuccess: () => { showSnackbar('Row inserted', 'success'); onSaved(); onClose(); },
    onError: (err: Error) => showSnackbar(err.message, 'error'),
  });

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>New row — {table}</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} mt={1}>
          {columns.map((col) => (
            <TextField
              key={col}
              label={col}
              size="small"
              fullWidth
              value={values[col] ?? ''}
              onChange={(e) => setValues((prev) => ({ ...prev, [col]: e.target.value }))}
            />
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => mutation.mutate()} disabled={mutation.isPending}>
          Insert
        </Button>
      </DialogActions>
    </Dialog>
  );
}

interface Props {
  db: string | null;
  table: string | null;
}

export function DataBrowserTab({ db, table }: Props) {
  const canWrite = usePermission('mysql.write');
  const showSnackbar = useSnackbar();
  const qc = useQueryClient();

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [sortCol, setSortCol] = useState('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [editingRow, setEditingRow] = useState<Record<string, unknown> | null>(null);
  const [editedCells, setEditedCells] = useState<Record<string, string>>({});
  const [newRowOpen, setNewRowOpen] = useState(false);

  const params = { page: page + 1, pageSize, sortCol, sortDir, search };
  const { data, isLoading, error } = useTableData(db, table, params);
  const { data: info } = useTableInfo(db, table);

  const invalidate = useCallback(() => {
    void qc.invalidateQueries({ queryKey: ['mysql', 'rows', db, table] });
    void qc.invalidateQueries({ queryKey: ['mysql', 'info', db, table] });
  }, [qc, db, table]);

  const saveMutation = useMutation({
    mutationFn: () => {
      const pk = data?.primaryKey;
      const pkVal = pk ? cellStr(editingRow![pk]) : '';
      return updateRow(db!, table!, pkVal, editedCells);
    },
    onSuccess: () => { showSnackbar('Row saved', 'success'); setEditingRow(null); invalidate(); },
    onError: (err: Error) => showSnackbar(err.message, 'error'),
  });

  const deleteMutation = useMutation({
    mutationFn: () => {
      const pk = data?.primaryKey;
      const pkVal = pk ? cellStr(editingRow![pk]) : '';
      return deleteRow(db!, table!, pkVal);
    },
    onSuccess: () => { showSnackbar('Row deleted', 'success'); setEditingRow(null); invalidate(); },
    onError: (err: Error) => showSnackbar(err.message, 'error'),
  });

  function handleSort(col: string) {
    if (sortCol === col) {
      setSortDir((d) => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
    setPage(0);
  }

  function startEdit(row: Record<string, unknown>) {
    setEditingRow(row);
    setEditedCells(Object.fromEntries(Object.entries(row).map(([k, v]) => [k, cellStr(v)])));
  }

  function cancelEdit() {
    setEditingRow(null);
    setEditedCells({});
  }

  const pk = data?.primaryKey ?? null;
  const editingPk = editingRow && pk ? cellStr(editingRow[pk]) : null;
  const exportingRef = useRef(false);

  async function handleExportCsv() {
    if (!db || !table || exportingRef.current) return;
    exportingRef.current = true;
    try {
      const result = await fetchTableRows(db, table, { page: 1, pageSize: 100000, sortCol, sortDir, search });
      if (!result.rows.length) return;
      const header = result.columns.join(',');
      const rows = result.rows.map((row) =>
        result.columns.map((col) => {
          const v = row[col];
          if (v === null || v === undefined) return '';
          const s = typeof v === 'object' ? JSON.stringify(v) : String(v);
          return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
        }).join(',')
      );
      const csv = [header, ...rows].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${table}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      showSnackbar('Export failed', 'error');
    } finally {
      exportingRef.current = false;
    }
  }

  if (!db || !table) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
        <Typography color="text.secondary">Select a table from the sidebar.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Metadata strip */}
      {info && (
        <Stack direction="row" spacing={3} sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Rows</Typography>
            <Typography variant="body2" fontFamily="monospace">~{info.rowCount.toLocaleString()}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Engine</Typography>
            <Typography variant="body2">{info.engine}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Charset</Typography>
            <Typography variant="body2" fontFamily="monospace">{info.charset}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Size</Typography>
            <Typography variant="body2">{fmtBytes(info.sizeBytes)}</Typography>
          </Box>
        </Stack>
      )}

      {/* Toolbar */}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 2, py: 1, flexShrink: 0 }}>
        <TextField
          size="small"
          placeholder="Search…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { setSearch(searchInput); setPage(0); } }}
          InputProps={{
            startAdornment: (
              <Tooltip title="Press Enter to search">
                <SearchIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
              </Tooltip>
            ),
          }}
          sx={{ width: 240 }}
        />
        <Box sx={{ flex: 1 }} />
        {canWrite && (
          <Button size="small" startIcon={<AddIcon />} onClick={() => setNewRowOpen(true)}>
            New Row
          </Button>
        )}
        {db && table && (
          <Tooltip title="Export current filter as CSV">
            <IconButton size="small" onClick={() => void handleExportCsv()}>
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>

      {/* Table */}
      <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        {isLoading && <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>}
        {error && <Alert severity="error" sx={{ m: 2 }}>{(error as Error).message}</Alert>}
        {data && (
          <TableContainer>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {data.columns.map((col) => (
                    <TableCell key={col} sx={{ whiteSpace: 'nowrap', bgcolor: 'background.paper' }}>
                      <TableSortLabel
                        active={sortCol === col}
                        direction={sortCol === col ? sortDir : 'asc'}
                        onClick={() => handleSort(col)}
                      >
                        <Typography variant="caption" fontFamily="monospace" fontWeight={700}>
                          {col}{col === pk ? ' 🔑' : ''}
                        </Typography>
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.rows.map((row, ri) => {
                  const rowPk = pk ? cellStr(row[pk]) : String(ri);
                  const isEditing = editingPk === rowPk && pk !== null;
                  return (
                    <TableRow
                      key={rowPk}
                      hover
                      selected={isEditing}
                      onClick={() => canWrite && startEdit(row)}
                      sx={{ cursor: canWrite ? 'pointer' : 'default' }}
                    >
                      {data.columns.map((col) => (
                        <TableCell key={col} sx={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {isEditing ? (
                            <TextField
                              size="small"
                              value={editedCells[col] ?? ''}
                              onChange={(e) => setEditedCells((prev) => ({ ...prev, [col]: e.target.value }))}
                              onClick={(e) => e.stopPropagation()}
                              sx={{ minWidth: 80 }}
                              variant="standard"
                            />
                          ) : (
                            <Typography variant="body2" fontFamily="monospace" fontSize="0.75rem" noWrap>
                              {row[col] === null ? <em style={{ color: '#666' }}>NULL</em> : cellStr(row[col])}
                            </Typography>
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })}
                {data.rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={data.columns.length || 1} align="center">
                      <Typography variant="body2" color="text.secondary" py={2}>No rows found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Pagination */}
      {data && (
        <TablePagination
          component="div"
          count={data.total}
          page={page}
          rowsPerPage={pageSize}
          rowsPerPageOptions={[10, 25, 50, 100]}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}
          sx={{ flexShrink: 0, borderTop: 1, borderColor: 'divider' }}
        />
      )}

      {/* Edit action bar */}
      {editingRow !== null && (
        <Paper elevation={0} sx={{ position: 'sticky', bottom: 0, px: 2, py: 1, borderTop: 1, borderColor: 'divider', display: 'flex', gap: 1, flexShrink: 0 }}>
          <Typography variant="body2" color="text.secondary" sx={{ flex: 1, alignSelf: 'center' }}>
            Editing row {editingPk !== null ? `(${pk} = ${editingPk})` : ''}
          </Typography>
          <Button size="small" onClick={cancelEdit}>Cancel</Button>
          <Button size="small" color="error" onClick={() => deleteMutation.mutate()} disabled={deleteMutation.isPending || !pk}>
            Delete Row
          </Button>
          <Button size="small" variant="contained" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending || !pk}>
            Save Changes
          </Button>
        </Paper>
      )}

      {newRowOpen && data && (
        <NewRowDialog
          db={db}
          table={table}
          columns={data.columns}
          onClose={() => setNewRowOpen(false)}
          onSaved={invalidate}
        />
      )}
    </Box>
  );
}
